import { AppDispatch } from "@Store/index";
import { setOpenAmberCopilot } from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import stripModalHistoryMarkers from "@Components/Modal/stripModalHistoryMarkers";

/**
 * Opens the Amber Copilot chatbot from inside a history-managed modal
 * (any modal using `useModal` / `BottomSheetOverlay` with `isHistoryPush`).
 *
 * Why this is needed:
 * - These modals push a history entry with `_modal`/`_modals` markers.
 * - On unmount, `useModal`'s cleanup calls `history.goBack()` if its entry
 *   is still on top — but that `goBack()` is async and races with the
 *   chatbot's own `pushState`, causing the chatbot to close immediately.
 *
 * What this does:
 * 1. Strips `_modal`/`_modals` from history state so `useModal` skips `goBack()`.
 * 2. Calls `window.history.back()` to cleanly pop the modal's history entry.
 * 3. Opens the chatbot only after the back-navigation completes (via popstate).
 * 4. Calls `onClose()` to unmount the modal from the UI.
 *
 * Usage:
 *   openCopilotFromModal(dispatch, onClose);
 */
const openCopilotFromModal = (dispatch: AppDispatch, onClose: () => void): void => {
  // Strip modal markers so useModal's cleanup won't call goBack()
  stripModalHistoryMarkers();

  // Open the chatbot only after the modal's history entry has been popped
  const openCopilotAfterNav = () => {
    window.removeEventListener("popstate", openCopilotAfterNav);
    dispatch(setOpenAmberCopilot(true));
  };
  window.addEventListener("popstate", openCopilotAfterNav);

  // Pop the modal's history entry
  window.history.back();

  // Unmount the modal from the UI
  onClose();
};

export default openCopilotFromModal;
